// src/pages/Campaigns/VolunteerManagement/EventCalendarView.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Grid, Chip, IconButton, Stack,
  CircularProgress, Alert, Card, CardContent, Tooltip, TextField, Divider,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon,
  Today as TodayIcon, People as PeopleIcon, LocationOn as LocationIcon,
  AccessTime as TimeIcon, Event as EventIcon, Campaign as CampaignIcon,
  Code as CodeIcon, ContentCopy as CopyIcon, FilterList as FilterIcon,
  ViewModule as MonthIcon, ViewWeek as WeekIcon, ViewDay as DayIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

// Setup date-fns localizer
const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Event type colors
const eventTypeColors = {
  RALLY: { bg: '#e3f2fd', border: '#1976d2', text: '#1565c0' },
  DONATION: { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
  FUNDRAISING: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
  COMMUNITY_SERVICE: { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' },
  OTHER: { bg: '#eceff1', border: '#607d8b', text: '#455a64' }
};

// Fill status colors
const fillStatusColors = {
  open: { bg: '#c8e6c9', text: '#2e7d32' },
  partial: { bg: '#fff9c4', text: '#f57f17' },
  full: { bg: '#ffcdd2', text: '#c62828' }
};

const EventCalendarView = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  
  // Filters
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialogs
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailDialog, setEventDetailDialog] = useState(false);
  const [embedDialog, setEmbedDialog] = useState(false);
  
  // Embed settings
  const [embedSettings, setEmbedSettings] = useState({
    type: 'iframe',
    width: '100%',
    height: '700px',
    defaultView: 'month'
  });
  const [embedUrl, setEmbedUrl] = useState('');

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate date range based on view
      let start, end;
      if (view === 'month') {
        start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      } else if (view === 'week') {
        start = startOfWeek(currentDate);
        end = new Date(start);
        end.setDate(end.getDate() + 6);
      } else {
        start = currentDate;
        end = currentDate;
      }
      
      // Extend range to catch multi-day events
      start = subDays(start, 7);
      end = addDays(end, 7);
      
      const params = {
        chatbot_id: chatbotId,
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd')
      };
      
      if (eventTypeFilter) params.event_type = eventTypeFilter;
      if (statusFilter) params.status = statusFilter;
      if (availabilityFilter) params.availability = availabilityFilter;
      
      const response = await axiosInstance.get('/api/events/calendar', { params });
      
      // Transform events for react-big-calendar
      const transformedEvents = response.data.data.map(event => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
      }));
      
      setEvents(transformedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [chatbotId, currentDate, view, eventTypeFilter, statusFilter, availabilityFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Load embed settings
  useEffect(() => {
    const loadEmbedSettings = async () => {
      try {
        const response = await axiosInstance.get('/api/events/calendar/embed-settings', {
          params: { chatbot_id: chatbotId }
        });
        if (response.data.success) {
          setEmbedUrl(response.data.data.calendar_url);
        }
      } catch (error) {
        console.error('Error loading embed settings:', error);
      }
    };
    loadEmbedSettings();
  }, [chatbotId]);

  // Navigation handlers
  const handleNavigate = (direction) => {
    if (view === 'month') {
      setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (view === 'week') {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : subDays(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Event click handler
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setEventDetailDialog(true);
  };

  // Custom event styling
  const eventStyleGetter = (event) => {
    const eventType = event.resource?.event_type || 'OTHER';
    const fillStatus = event.resource?.fill_status || 'open';
    const colors = eventTypeColors[eventType];
    const statusColors = fillStatusColors[fillStatus];
    
    return {
      style: {
        backgroundColor: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        color: colors.text,
        borderRadius: '4px',
        padding: '2px 6px',
        fontSize: '0.85em',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
      }
    };
  };

  // Custom event component
  const EventComponent = ({ event }) => {
    const fillStatus = event.resource?.fill_status || 'open';
    const statusColors = fillStatusColors[fillStatus];
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {event.title}
        </Typography>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: statusColors.text,
            flexShrink: 0
          }}
        />
      </Box>
    );
  };

  // Generate embed code
  const generateEmbedCode = () => {
    const url = `${embedUrl}?view=${embedSettings.defaultView}`;
    switch (embedSettings.type) {
      case 'iframe':
        return `<iframe src="${url}" width="${embedSettings.width}" height="${embedSettings.height}" frameborder="0" style="border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"></iframe>`;
      case 'popup':
        return `<script>function openEventsCalendar(){window.open('${url}','events','width=900,height=700,scrollbars=yes');}</script><button onclick="openEventsCalendar()" style="padding:12px 24px;background:#1976d2;color:white;border:none;border-radius:4px;cursor:pointer;">View Events Calendar</button>`;
      case 'button':
        return `<a href="${url}" target="_blank" style="display:inline-block;padding:12px 24px;background:#1976d2;color:white;text-decoration:none;border-radius:4px;">View Events Calendar</a>`;
      default:
        return '';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const clearFilters = () => {
    setEventTypeFilter('');
    setStatusFilter('');
    setAvailabilityFilter('');
  };

  const getViewTitle = () => {
    if (view === 'month') {
      return format(currentDate, 'MMMM yyyy');
    } else if (view === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    }
  };

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight={700}>
            Events Calendar
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<CodeIcon />}
            onClick={() => setEmbedDialog(true)}
          >
            Embed Calendar
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/chatbot/${chatbotId}/campaigns/events`)}
          >
            List View
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={eventTypeFilter}
                  onChange={(e) => setEventTypeFilter(e.target.value)}
                  label="Event Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="RALLY">Rally</MenuItem>
                  <MenuItem value="DONATION">Donation</MenuItem>
                  <MenuItem value="FUNDRAISING">Fundraising</MenuItem>
                  <MenuItem value="COMMUNITY_SERVICE">Community Service</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                  <MenuItem value="ONGOING">Ongoing</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Availability</InputLabel>
                <Select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  label="Availability"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open (Spots Available)</MenuItem>
                  <MenuItem value="partial">Partially Filled</MenuItem>
                  <MenuItem value="full">Full</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button onClick={clearFilters} size="small">
                Clear Filters
              </Button>
            </Grid>
          </Grid>
          
          {/* Legend */}
          <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography variant="caption" fontWeight={600}>Event Types:</Typography>
            {Object.entries(eventTypeColors).map(([type, colors]) => (
              <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: colors.bg, border: `2px solid ${colors.border}` }} />
                <Typography variant="caption">{type.replace('_', ' ')}</Typography>
              </Box>
            ))}
            <Divider orientation="vertical" flexItem />
            <Typography variant="caption" fontWeight={600}>Availability:</Typography>
            {Object.entries(fillStatusColors).map(([status, colors]) => (
              <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.text }} />
                <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{status}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Calendar Navigation */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => handleNavigate('prev')}>
              <ArrowBackIcon />
            </IconButton>
            <IconButton onClick={() => handleNavigate('next')}>
              <ArrowForwardIcon />
            </IconButton>
            <Button startIcon={<TodayIcon />} onClick={handleToday}>
              Today
            </Button>
            <Typography variant="h6" sx={{ ml: 2 }}>
              {getViewTitle()}
            </Typography>
          </Box>
          
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(e, newView) => newView && setView(newView)}
            size="small"
          >
            <ToggleButton value="month">
              <Tooltip title="Month View">
                <MonthIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="week">
              <Tooltip title="Week View">
                <WeekIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="day">
              <Tooltip title="Day View">
                <DayIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Calendar */}
      <Paper sx={{ p: 2, height: 700 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            view={view}
            onView={setView}
            date={currentDate}
            onNavigate={setCurrentDate}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            components={{
              event: EventComponent
            }}
            toolbar={false}
            popup
            selectable={false}
          />
        )}
      </Paper>

      {/* Event Detail Dialog */}
      <Dialog open={eventDetailDialog} onClose={() => setEventDetailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedEvent?.title}</Typography>
            <IconButton onClick={() => setEventDetailDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={selectedEvent.resource?.event_type?.replace('_', ' ')} 
                  size="small" 
                  sx={{ mr: 1 }}
                />
                <Chip 
                  label={selectedEvent.resource?.fill_status?.toUpperCase()} 
                  size="small"
                  sx={{ 
                    bgcolor: fillStatusColors[selectedEvent.resource?.fill_status]?.bg,
                    color: fillStatusColors[selectedEvent.resource?.fill_status]?.text
                  }}
                />
              </Box>
              
              {selectedEvent.resource?.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedEvent.resource.description}
                </Typography>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {format(selectedEvent.start, 'MMM d, yyyy')}
                      {selectedEvent.resource?.event_end_date !== selectedEvent.resource?.event_start_date && 
                        ` - ${format(selectedEvent.end, 'MMM d, yyyy')}`}
                    </Typography>
                  </Box>
                </Grid>
                {selectedEvent.resource?.event_start_time && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimeIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {selectedEvent.resource.event_start_time} - {selectedEvent.resource.event_end_time}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2">{selectedEvent.resource?.location}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {selectedEvent.resource?.total_assigned_volunteers} / {selectedEvent.resource?.total_required_volunteers} volunteers
                      ({selectedEvent.resource?.fill_percentage}% filled)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {/* Time Slots */}
              {selectedEvent.resource?.time_slots?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Time Slots</Typography>
                  {selectedEvent.resource.time_slots.map((slot) => (
                    <Chip
                      key={slot.id}
                      label={`${slot.slot_name} (${slot.start_time} - ${slot.end_time})`}
                      size="small"
                      variant="outlined"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDetailDialog(false)}>Close</Button>
          <Button 
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => {
              setEventDetailDialog(false);
              navigate(`/chatbot/${chatbotId}/campaigns/event-volunteers?event_id=${selectedEvent?.id}`);
            }}
          >
            View Volunteers
          </Button>
          <Button 
            variant="contained"
            startIcon={<CampaignIcon />}
            onClick={() => {
              setEventDetailDialog(false);
              navigate(`/chatbot/${chatbotId}/campaigns/outreach/${selectedEvent?.id}`);
            }}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Embed Dialog */}
      <Dialog open={embedDialog} onClose={() => setEmbedDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Embed Events Calendar</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Embed Type</InputLabel>
            <Select
              value={embedSettings.type}
              label="Embed Type"
              onChange={(e) => setEmbedSettings({ ...embedSettings, type: e.target.value })}
            >
              <MenuItem value="iframe">iFrame (Inline)</MenuItem>
              <MenuItem value="popup">Popup Window</MenuItem>
              <MenuItem value="button">Button Link</MenuItem>
            </Select>
          </FormControl>
          
          {embedSettings.type === 'iframe' && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Width"
                  value={embedSettings.width}
                  onChange={(e) => setEmbedSettings({ ...embedSettings, width: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Height"
                  value={embedSettings.height}
                  onChange={(e) => setEmbedSettings({ ...embedSettings, height: e.target.value })}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Default View</InputLabel>
                  <Select
                    value={embedSettings.defaultView}
                    label="Default View"
                    onChange={(e) => setEmbedSettings({ ...embedSettings, defaultView: e.target.value })}
                  >
                    <MenuItem value="month">Month</MenuItem>
                    <MenuItem value="week">Week</MenuItem>
                    <MenuItem value="day">Day</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          
          <TextField
            fullWidth
            label="Embed Code"
            value={generateEmbedCode()}
            multiline
            rows={6}
            margin="normal"
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmbedDialog(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<CopyIcon />}
            onClick={() => {
              copyToClipboard(generateEmbedCode());
              setEmbedDialog(false);
            }}
          >
            Copy Code
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventCalendarView;